import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Modal,
  Snackbar,
  Typography,
} from "@mui/material";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SimCardDownloadIcon from "@mui/icons-material/SimCardDownload";
import filterEmailTemplate from "../../../assets/csv/filter-email-template.csv";

const mainContainer = {
  backgroundColor: "white",
  width: "40rem",
  margin: "0 auto",
  padding: "1.5em 2.5em",
  borderRadius: "8px",
};

const headerContainer = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const uploadContainer = {
  display: "flex",
  backgroundColor: "#B0D3E1",
  padding: "5rem 0",
  width: "100%",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "5px",
  border: "2px solid white",
  cursor: "pointer",
  "&:hover": {
    border: "2px dashed #808080",
  },
};

const textStyles = {
  fontSize: "1rem",
  fontWeight: "500",
  margin: "1rem 0rem",
};

const divider = { margin: "1.5rem 0rem" };

const actionContainer = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "1rem",
};

export default function UploadFilterEmails({ open, setOpen, onUpload }) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");

  const closeModal = () => setOpen(false);

  const handleDrag = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleUploadClick = () => {
    inputRef.current?.click();
  };

  const handleSelectedFile = async (file) => {
    if (!file) return;
    if (file.type !== "text/csv" && !file.name.toLowerCase().endsWith(".csv")) {
      setError("Please only upload CSV files.");
      return;
    }

    try {
      await onUpload(file);
    } catch (uploadError) {
      console.error(uploadError);
      setError("Failed to upload CSV file.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Container sx={mainContainer}>
        <Box sx={headerContainer}>
          <Typography sx={textStyles}>Upload CSV</Typography>
          <IconButton onClick={closeModal}>
            <HighlightOffIcon />
          </IconButton>
        </Box>
        <Divider sx={divider} />
        <Box
          sx={uploadContainer}
          onDragOver={handleDrag}
          onDrop={(event) => {
            event.preventDefault();
            const files = Array.from(event.dataTransfer.files);
            handleSelectedFile(files[0]);
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onClick={handleUploadClick}
        >
          <UploadFileIcon
            sx={{ width: "3rem", height: "3rem", color: "#808080" }}
          />
          <Typography sx={{ ...textStyles, fontWeight: 400 }}>
            Drop or Click here to upload CSV
          </Typography>
          <input
            type="file"
            name="file"
            onChange={(e) => handleSelectedFile(e.target.files?.[0])}
            accept=".csv"
            multiple={false}
            style={{ display: "none" }}
            ref={inputRef}
          />
        </Box>
        <Divider sx={divider} />
        <Box sx={actionContainer}>
          <Button
            href={filterEmailTemplate}
            download="filter-email-template.csv"
            variant="outlined"
            color="primary"
            startIcon={<SimCardDownloadIcon />}
          >
            Download Sample CSV
          </Button>
          <Button color="info" variant="contained" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
        <Snackbar
          open={Boolean(error)}
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
          onClose={() => setError("")}
          action={
            <IconButton onClick={() => setError("")}>
              <HighlightOffIcon />
            </IconButton>
          }
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </Modal>
  );
}
